"use client"
import { useState, useEffect } from "react"
import Table from "../../../../components/table"
import styles from "./medicationOptions.module.css"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTrash, faFilePen, faTimes } from "@fortawesome/free-solid-svg-icons"
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom"
import { useMutation, useQueryClient } from "@tanstack/react-query";

import searchIcon from "../../../../assets/images/search.png"
import { addMedicationOption, deleteMedicationOption, editMedicationOption, getMedicationOptions } from "../../../../services/admin/medicationOptions"
import toast from "react-hot-toast"




const MedicationOptions = () => {
  const { id: location_id } = useParams();
  const queryClient = useQueryClient();

  const { data: medicationData, isLoading } = useQuery({
    queryKey: ["medicationOptions", location_id],
    queryFn: () => getMedicationOptions(location_id),
  });

  const delete_mutation = useMutation(
    () => deleteMedicationOption(medicationToDelete.id, location_id),
    {
      onSuccess: () => {
        toast.success("Medication deleted successfully.");
        queryClient.invalidateQueries(["medicationOptions"]);
        closeDeleteModal();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || "Failed to delete medication.");
      },
    });

  const [nameSearch, setNameSearch] = useState("")
  const [strengthSearch, setStrengthSearch] = useState("")
  const [filteredData, setFilteredData] = useState(medicationData || []);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editFormData, setEditFormData] = useState(null)
  const [selectedMedications, setSelectedMedications] = useState([])
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [medicationToDelete, setMedicationToDelete] = useState(null)

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilteredData(
        medicationData?.filter(
          (med) =>
            med.name.toLowerCase().includes(nameSearch.toLowerCase()) &&
            med.strength.toLowerCase().includes(strengthSearch.toLowerCase()),
        ),
      )
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [nameSearch, strengthSearch, medicationData])

  const handleSelectionChange = (selectedIds) => {
    setSelectedMedications(selectedIds)
  }
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      closeEditModal()
    }
  }

  const handleDeleteOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      closeDeleteModal()
    }
  }

  const handleEditMedication = (medication) => {
    setEditFormData(medication)
    setIsEditModalOpen(true)
  }

  const handleDeleteMedication = (medicationId) => {
    const medicationToDelete = medicationData.find((med) => med.id === medicationId)
    setMedicationToDelete(medicationToDelete)
    setIsDeleteModalOpen(true)
  }

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false)
    setMedicationToDelete(null)
  }

  const handleEditFormChange = (e) => {
    const { name, value } = e.target
    setEditFormData((prev) => ({ ...prev, [name]: value }))
  }

  const edit_mutation = useMutation(
    () => editMedicationOption({ id: editFormData.id, data: editFormData, location_id }),
    {
      onSuccess: () => {
        toast.success("Medication Updated successfully.");
        queryClient.invalidateQueries(["medicationOptions"]);
        closeEditModal();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || "Failed to update medication.");
      },
    });

  const handleEditFormSubmit = (e) => {
    e.preventDefault();
    edit_mutation.mutate();
  }

  const handleDelete = async (e) => {
    e.preventDefault();

    delete_mutation.mutate();
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false)
    setEditFormData(null)
  }

  const columns = [
    { key: "name", label: "Medication Name", alignLeft: true },
    { key: "strength", label: "Strength", alignLeft: true },
    { key: "directions", label: "Directions", alignLeft: true },
    {
      key: "actions",
      label: "Actions",
      sorting: false,
      render: (row) => (
        <div className={styles.action_btns}>
          <span
            onClick={(e) => {
              e.stopPropagation()
              handleEditMedication(row)
            }}
          >
            <FontAwesomeIcon icon={faFilePen} />
          </span>
          <span
            onClick={(e) => {
              e.stopPropagation()
              handleDeleteMedication(row.id)
            }}
          >
            <FontAwesomeIcon icon={faTrash} className={styles.delete} />
          </span>
        </div>
      ),
    },
  ]

  return (
    <div className={styles.medicationContainer}>
      <div className={styles.searchbar_container}>
        <div className={styles.input_wrapper}>
          <input
            type="text"
            placeholder="Search by Name"
            value={nameSearch}
            onChange={(e) => setNameSearch(e.target.value)}
          />
          <img src={searchIcon || "/placeholder.svg"} alt="Search" />
        </div>
        <div className={styles.input_wrapper}>
          <input
            type="text"
            placeholder="Search by Strength"
            value={strengthSearch}
            onChange={(e) => setStrengthSearch(e.target.value)}
          />
          <img src={searchIcon || "/placeholder.svg"} alt="Search" />
        </div>
      </div>

      <hr className={styles.saparator} />

      <Table
        columns={columns}
        data={filteredData || []}
        height={400}
        loading={false}
        onSelectionChange={handleSelectionChange}
      />

      {selectedMedications.length > 0 && (
        <div className={styles.selectedInfo}>
          <p>{selectedMedications.length} medication(s) selected</p>
        </div>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className={styles.overlay} onClick={handleOverlayClick}>
          <div className={styles.modal}>
            <div className={styles.comp_header}>
              <h3 className={styles.comp_heading}>Edit Medication</h3>

            </div>

            <form onSubmit={handleEditFormSubmit}>
              <div className={`${styles.comp_wrapper} ${edit_mutation.isLoading ? styles.loading : ""}`}>
                <div className={styles.input_group}>
                  <div className={styles.input_field}>
                    <label htmlFor="name">Medication Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={editFormData?.name || ""}
                      onChange={handleEditFormChange}
                      required
                    />
                  </div>
                </div>
                <div className={styles.input_group}>
                  <div className={styles.input_field}>
                    <label htmlFor="strength">Strength</label>
                    <input
                      type="text"
                      id="strength"
                      name="strength"
                      value={editFormData?.strength || ""}
                      onChange={handleEditFormChange}
                      required
                    />
                  </div>
                </div>
                <div className={styles.input_group}>
                  <div className={styles.input_field}>
                    <label htmlFor="directions">Directions</label>
                    <textarea
                      id="directions"
                      name="directions"
                      value={editFormData?.directions || ""}
                      onChange={handleEditFormChange}
                      required
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              <div className={`${styles.submit_btn} ${styles.split}`}>
                <button
                  type="submit"
                  disabled={edit_mutation.isLoading}
                >
                  {edit_mutation.isLoading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className={styles.overlay} onClick={handleDeleteOverlayClick}>
          <div className={styles.delete_modal}>
            <div className={styles.comp_header}>
              <h3 className={styles.comp_heading}>Confirm Deletion</h3>
              <span onClick={closeDeleteModal} style={{ cursor: "pointer" }}>
                <FontAwesomeIcon icon={faTimes} />
              </span>
            </div>

            <div className={styles.comp_wrapper}>
              <p>Are you sure you want to delete <b>{medicationToDelete?.name}</b>?</p>
            </div>

            <div className={`${styles.submit_btn} ${styles.split}`}>
              <button type="button" onClick={closeDeleteModal}>
                Cancel
              </button>
              <button onClick={handleDelete} disabled={delete_mutation.isLoading}>
                {delete_mutation.isLoading ? "Deleting..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MedicationOptions

